<template>
  <div class="c-config-input-type">
    <div class="c-config-input-info">
      <label>{{ input.label }}</label>
      <p>{{ input.description }}</p>
    </div>

    <div class="c-config-input-actions">
      <div
        class="c-segmented"
        :class="{ 'is-disabled': disabled }"
        role="radiogroup"
        :aria-label="input.label"
      >
        <label
          v-for="option in options"
          :key="option.value"
          class="c-segmented__option"
          :class="{ 'is-selected': selected === option.value }"
        >
          <input
            v-model="selected"
            class="c-segmented__input"
            type="radio"
            :name="radioGroupName"
            :value="option.value"
            :disabled="disabled"
            @change="setValue(option.value)"
          >
          <span
            class="c-segmented__dot"
            :class="{ 'is-positive': isPositive(option.label) }"
          />
          <span class="c-segmented__label">{{ option.label }}</span>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RadioFormField',
  props: {
    input: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
  },

  data() {
    return {
      options: [],
      selected: '0',
    };
  },

  computed: {
    radioGroupName() {
      return `radio-${this.input.key || 'field'}-${this._uid}`;
    },
  },

  created() {
    const options = JSON.parse(this.input.values)

    Object.entries(options).forEach(([value, label]) => {
      this.options.push({
        value,
        label,
      });
    });

    this.selected = String(this.input.value);
  },

  methods: {
    // "enabled" / "on" / "yes" options get a green status dot when selected
    isPositive(label) {
      return /^(enabled?|on|yes|true|active|visible|show)$/i.test(String(label).trim());
    },

    setValue($event) {
      this.$emit('update', {
        value: $event,
      })
    },
  }

};
</script>

<style lang="scss" scoped>
.c-segmented {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 3px;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background-color: var(--bg-subtle);
  font-family: var(--font-sans);

  &.is-disabled {
    opacity: 0.55;

    .c-segmented__option {
      cursor: not-allowed;
    }
  }
}

.c-segmented__option {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  margin: 0;
  padding: 0 14px 0 12px;
  border-radius: var(--radius-sm);
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
  transition: background-color var(--duration-fast) ease, color var(--duration-fast) ease,
    box-shadow var(--duration-fast) ease;

  &:hover {
    color: var(--text-primary);
  }

  &:focus-within {
    box-shadow: var(--focus-ring);
  }

  &.is-selected {
    background-color: var(--bg-surface);
    box-shadow: var(--shadow-sm);
    color: var(--text-primary);
    font-weight: 600;
  }
}

// the native radio stays in the DOM for keyboard + screen readers
.c-segmented__input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  pointer-events: none;
}

.c-segmented__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--border-strong);
  transition: background-color var(--duration-fast) ease;

  .is-selected & {
    background-color: var(--text-muted);
  }

  .is-selected &.is-positive {
    background-color: #17b26a;
  }
}

.c-segmented__label::first-letter {
  text-transform: uppercase;
}
</style>
